import * as THREE from 'three';
import { PhysicsBody } from '../types';

export type TargetType = 'fighter' | 'cruiser' | 'station';
export type AIState = 'patrol' | 'chase' | 'attack' | 'evade';

/**
 * A targetable object (enemy ship, space station) that can be destroyed.
 */
export class TargetableObject {
  public readonly id: string;
  public readonly type: TargetType;
  public readonly mesh: THREE.Group;
  public physicsBody: PhysicsBody;

  // Target state
  private alive: boolean = true;
  private health: number;
  private maxHealth: number;

  // Visual elements
  private mainMesh: THREE.Group;
  private targetIndicator: THREE.Group;
  private healthBar: THREE.Mesh;
  private healthBarBg: THREE.Mesh;

  // Animation
  private rotationSpeed: THREE.Vector3;

  // AI State
  private aiState: AIState = 'patrol';
  private aiTarget: THREE.Vector3 = new THREE.Vector3();
  private patrolCenter: THREE.Vector3;
  private patrolRadius: number = 50;
  private patrolAngle: number = 0;
  private lastFireTime: number = 0;
  private fireRate: number; // Seconds between shots
  private detectionRange: number;
  private attackRange: number;
  private evadeHealthThreshold: number = 0.3;
  private maxSpeed: number;
  private acceleration: number;

  // Projectile spawning callback
  public onFireProjectile?: (position: THREE.Vector3, direction: THREE.Vector3, velocity: THREE.Vector3) => void;

  constructor(
    type: TargetType,
    position: THREE.Vector3,
    velocity: THREE.Vector3 = new THREE.Vector3()
  ) {
    this.id = `target_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.type = type;

    // Set health based on type
    switch (type) {
      case 'fighter':
        this.maxHealth = 1;
        break;
      case 'cruiser':
        this.maxHealth = 3;
        break;
      case 'station':
        this.maxHealth = 5;
        break;
    }
    this.health = this.maxHealth;

    // Set AI parameters based on type
    this.patrolCenter = position.clone();
    switch (type) {
      case 'fighter':
        this.fireRate = 0.8;
        this.detectionRange = 150;
        this.attackRange = 80;
        this.maxSpeed = 40;
        this.acceleration = 25;
        this.patrolRadius = 30;
        break;
      case 'cruiser':
        this.fireRate = 1.5;
        this.detectionRange = 200;
        this.attackRange = 120;
        this.maxSpeed = 20;
        this.acceleration = 10;
        this.patrolRadius = 50;
        break;
      case 'station':
        this.fireRate = 2.0;
        this.detectionRange = 250;
        this.attackRange = 200;
        this.maxSpeed = 0;
        this.acceleration = 0;
        this.patrolRadius = 0;
        break;
    }

    // Create container
    this.mesh = new THREE.Group();
    this.mesh.name = this.id;
    this.mesh.userData = { targetable: this };

    // Create the ship/station mesh
    this.mainMesh = this.createMesh();
    this.mesh.add(this.mainMesh);

    // Create targeting indicator (red chevron pointing down)
    this.targetIndicator = this.createTargetIndicator();
    this.mesh.add(this.targetIndicator);

    // Create health bar
    const { bar, bg } = this.createHealthBar();
    this.healthBar = bar;
    this.healthBarBg = bg;
    this.mesh.add(this.healthBarBg);
    this.mesh.add(this.healthBar);

    // Set position
    this.mesh.position.copy(position);

    // Random rotation speed
    this.rotationSpeed = new THREE.Vector3(
      (Math.random() - 0.5) * 0.2,
      (Math.random() - 0.5) * 0.3,
      (Math.random() - 0.5) * 0.1
    );

    // Create physics body
    const radius = this.getRadius();
    const mass = this.getMass();

    this.physicsBody = {
      id: this.id,
      position: position.clone(),
      velocity: velocity.clone(),
      acceleration: new THREE.Vector3(),
      mass: mass,
      radius: radius,
      isStatic: type === 'station', // Stations don't move
      object3D: this.mesh,
    };
  }

  private getRadius(): number {
    switch (this.type) {
      case 'fighter': return 3;
      case 'cruiser': return 8;
      case 'station': return 15;
    }
  }

  private getMass(): number {
    switch (this.type) {
      case 'fighter': return 5;
      case 'cruiser': return 50;
      case 'station': return 500;
    }
  }

  private createMesh(): THREE.Group {
    const group = new THREE.Group();

    switch (this.type) {
      case 'fighter':
        this.createFighterMesh(group);
        break;
      case 'cruiser':
        this.createCruiserMesh(group);
        break;
      case 'station':
        this.createStationMesh(group);
        break;
    }

    return group;
  }

  private createFighterMesh(group: THREE.Group): void {
    // Small enemy fighter - angular aggressive design
    // Using BasicMaterial for visibility without lighting dependency
    const bodyMat = new THREE.MeshBasicMaterial({
      color: 0x884444,
    });

    const accentMat = new THREE.MeshBasicMaterial({
      color: 0xff4444,
    });

    // Main body - elongated octahedron
    const body = new THREE.Mesh(
      new THREE.OctahedronGeometry(2, 0),
      bodyMat
    );
    body.scale.set(1, 0.4, 2);
    group.add(body);

    // Wings
    const wingGeo = new THREE.BoxGeometry(6, 0.2, 2);
    const leftWing = new THREE.Mesh(wingGeo, bodyMat);
    leftWing.position.set(-2, 0, 0);
    leftWing.rotation.z = 0.2;
    group.add(leftWing);

    const rightWing = new THREE.Mesh(wingGeo, bodyMat);
    rightWing.position.set(2, 0, 0);
    rightWing.rotation.z = -0.2;
    group.add(rightWing);

    // Cockpit (red glow)
    const cockpit = new THREE.Mesh(
      new THREE.SphereGeometry(0.8, 8, 8),
      accentMat
    );
    cockpit.position.z = 1.5;
    cockpit.scale.set(1, 0.5, 1);
    group.add(cockpit);

    // Engine glow
    const engine = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.8, 1, 8),
      new THREE.MeshBasicMaterial({ color: 0xff4400 })
    );
    engine.rotation.x = Math.PI / 2;
    engine.position.z = -2.5;
    group.add(engine);

    // Point light for engine
    const engineLight = new THREE.PointLight(0xff4400, 1, 10);
    engineLight.position.z = -3;
    group.add(engineLight);
  }

  private createCruiserMesh(group: THREE.Group): void {
    // Medium enemy cruiser - bulkier design
    const bodyMat = new THREE.MeshBasicMaterial({
      color: 0x556677,
    });

    const accentMat = new THREE.MeshBasicMaterial({
      color: 0xff5555,
    });

    // Main hull
    const hull = new THREE.Mesh(
      new THREE.BoxGeometry(4, 3, 12),
      bodyMat
    );
    group.add(hull);

    // Bridge section
    const bridge = new THREE.Mesh(
      new THREE.BoxGeometry(3, 2, 3),
      bodyMat
    );
    bridge.position.set(0, 2, 2);
    group.add(bridge);

    // Engine section
    const engineSection = new THREE.Mesh(
      new THREE.CylinderGeometry(2, 2.5, 4, 8),
      bodyMat
    );
    engineSection.rotation.x = Math.PI / 2;
    engineSection.position.z = -6;
    group.add(engineSection);

    // Weapon pods
    const podGeo = new THREE.BoxGeometry(1.5, 1, 4);
    const leftPod = new THREE.Mesh(podGeo, accentMat);
    leftPod.position.set(-3, 0, 2);
    group.add(leftPod);

    const rightPod = new THREE.Mesh(podGeo, accentMat);
    rightPod.position.set(3, 0, 2);
    group.add(rightPod);

    // Engine glows
    for (let i = -1; i <= 1; i++) {
      const glow = new THREE.Mesh(
        new THREE.CircleGeometry(0.8, 16),
        new THREE.MeshBasicMaterial({ color: 0xff6600 })
      );
      glow.position.set(i * 1.2, 0, -8);
      group.add(glow);
    }

    // Point light
    const light = new THREE.PointLight(0xff4400, 2, 20);
    light.position.z = -8;
    group.add(light);
  }

  private createStationMesh(group: THREE.Group): void {
    // Space station - rotating ring design
    const bodyMat = new THREE.MeshBasicMaterial({
      color: 0x778899,
    });

    const accentMat = new THREE.MeshBasicMaterial({
      color: 0xff6666,
    });

    // Central hub
    const hub = new THREE.Mesh(
      new THREE.SphereGeometry(5, 16, 16),
      bodyMat
    );
    group.add(hub);

    // Rotating ring
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(12, 2, 8, 32),
      bodyMat
    );
    ring.name = 'rotatingRing';
    group.add(ring);

    // Second ring (perpendicular)
    const ring2 = new THREE.Mesh(
      new THREE.TorusGeometry(10, 1.5, 8, 32),
      bodyMat
    );
    ring2.rotation.x = Math.PI / 2;
    ring2.name = 'rotatingRing2';
    group.add(ring2);

    // Docking arms
    for (let i = 0; i < 4; i++) {
      const arm = new THREE.Mesh(
        new THREE.BoxGeometry(2, 2, 8),
        bodyMat
      );
      const angle = (i / 4) * Math.PI * 2;
      arm.position.set(
        Math.cos(angle) * 8,
        0,
        Math.sin(angle) * 8
      );
      arm.rotation.y = -angle;
      group.add(arm);

      // Red lights on arms
      const light = new THREE.Mesh(
        new THREE.SphereGeometry(0.5, 8, 8),
        accentMat
      );
      light.position.set(
        Math.cos(angle) * 12,
        0,
        Math.sin(angle) * 12
      );
      group.add(light);
    }

    // Central antenna
    const antenna = new THREE.Mesh(
      new THREE.CylinderGeometry(0.3, 0.5, 10, 8),
      bodyMat
    );
    antenna.position.y = 8;
    group.add(antenna);

    // Blinking light on top
    const topLight = new THREE.Mesh(
      new THREE.SphereGeometry(0.8, 8, 8),
      accentMat
    );
    topLight.position.y = 13;
    topLight.name = 'blinkingLight';
    group.add(topLight);

    // Point lights
    const light1 = new THREE.PointLight(0xff4444, 1, 30);
    light1.position.y = 13;
    group.add(light1);
  }

  private createTargetIndicator(): THREE.Group {
    const group = new THREE.Group();

    // Red chevron pointing down at target
    const chevronMat = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });

    // Create chevron shape (downward pointing arrow/carrot)
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(-1.5, 2);
    shape.lineTo(-0.8, 2);
    shape.lineTo(0, 0.8);
    shape.lineTo(0.8, 2);
    shape.lineTo(1.5, 2);
    shape.closePath();

    const chevronGeo = new THREE.ShapeGeometry(shape);
    const chevron = new THREE.Mesh(chevronGeo, chevronMat);

    // Scale based on target type
    const scale = this.type === 'station' ? 3 : (this.type === 'cruiser' ? 2 : 1.2);
    chevron.scale.set(scale, scale, 1);

    group.add(chevron);

    // Outer diamond frame
    const framePoints = [
      new THREE.Vector3(0, -1.5 * scale, 0),
      new THREE.Vector3(2 * scale, 1 * scale, 0),
      new THREE.Vector3(0, 3.5 * scale, 0),
      new THREE.Vector3(-2 * scale, 1 * scale, 0),
      new THREE.Vector3(0, -1.5 * scale, 0),
    ];
    const frameGeo = new THREE.BufferGeometry().setFromPoints(framePoints);
    const frameMat = new THREE.LineBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.7,
    });
    const frame = new THREE.Line(frameGeo, frameMat);
    group.add(frame);

    // Position above the target
    const height = this.getRadius() * 2 + 5;
    group.position.y = height;

    // Make it always face camera (billboard)
    group.userData.isBillboard = true;

    return group;
  }

  private createHealthBar(): { bar: THREE.Mesh; bg: THREE.Mesh } {
    const width = this.getRadius() * 1.5;
    const height = 0.5;

    // Background
    const bgGeo = new THREE.PlaneGeometry(width, height);
    const bgMat = new THREE.MeshBasicMaterial({
      color: 0x330000,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    });
    const bg = new THREE.Mesh(bgGeo, bgMat);

    // Health bar
    const barGeo = new THREE.PlaneGeometry(width * 0.95, height * 0.7);
    const barMat = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    const bar = new THREE.Mesh(barGeo, barMat);
    bar.position.z = 0.01;

    // Position above target indicator
    const yPos = this.getRadius() * 2 + 8;
    bg.position.y = yPos;
    bar.position.y = yPos;

    bg.userData.isBillboard = true;
    bar.userData.isBillboard = true;

    return { bar, bg };
  }

  public update(deltaTime: number, cameraPosition: THREE.Vector3, elapsedTime: number = 0): void {
    if (!this.alive) return;

    // Update AI behavior (not for stations movement, but they can still shoot)
    this.updateAI(deltaTime, cameraPosition, elapsedTime);

    // Rotate the main mesh (gentle spin for visual interest)
    if (this.type !== 'station') {
      // Face the direction of movement for fighters/cruisers
      if (this.physicsBody.velocity.lengthSq() > 1) {
        const targetQuat = new THREE.Quaternion();
        const lookDir = this.physicsBody.velocity.clone().normalize();
        const up = new THREE.Vector3(0, 1, 0);
        const matrix = new THREE.Matrix4().lookAt(new THREE.Vector3(), lookDir, up);
        targetQuat.setFromRotationMatrix(matrix);
        this.mainMesh.quaternion.slerp(targetQuat, deltaTime * 3);
      }
    } else {
      // Station: gentle rotation
      this.mainMesh.rotation.y += this.rotationSpeed.y * deltaTime * 0.3;
    }

    // Special animations for station
    if (this.type === 'station') {
      const ring = this.mainMesh.getObjectByName('rotatingRing');
      const ring2 = this.mainMesh.getObjectByName('rotatingRing2');
      if (ring) ring.rotation.z += deltaTime * 0.2;
      if (ring2) ring2.rotation.y += deltaTime * 0.15;

      // Blink the top light by toggling visibility
      const blinkLight = this.mainMesh.getObjectByName('blinkingLight') as THREE.Mesh;
      if (blinkLight) {
        blinkLight.visible = Math.sin(Date.now() * 0.005) > 0;
      }
    }

    // Billboard - make indicators face camera
    this.billboardToCamera(this.targetIndicator, cameraPosition);
    this.billboardToCamera(this.healthBar, cameraPosition);
    this.billboardToCamera(this.healthBarBg, cameraPosition);

    // Pulse the target indicator - faster when in attack mode
    const pulseSpeed = this.aiState === 'attack' ? 0.015 : 0.005;
    const pulse = 1 + Math.sin(Date.now() * pulseSpeed) * 0.1;
    this.targetIndicator.scale.set(pulse, pulse, pulse);

    // Update health bar scale
    const healthPercent = this.health / this.maxHealth;
    this.healthBar.scale.x = healthPercent;
    this.healthBar.position.x = -(1 - healthPercent) * this.getRadius() * 0.75 * 0.5;
  }

  private updateAI(deltaTime: number, playerPosition: THREE.Vector3, elapsedTime: number): void {
    const myPosition = this.mesh.position;
    const distanceToPlayer = myPosition.distanceTo(playerPosition);

    // Determine AI state based on distance and health
    const healthPercent = this.health / this.maxHealth;

    if (healthPercent <= this.evadeHealthThreshold && this.type === 'fighter') {
      this.aiState = 'evade';
    } else if (distanceToPlayer <= this.attackRange) {
      this.aiState = 'attack';
    } else if (distanceToPlayer <= this.detectionRange) {
      this.aiState = 'chase';
    } else {
      this.aiState = 'patrol';
    }

    // Execute behavior based on state
    switch (this.aiState) {
      case 'patrol':
        this.executePatrol(deltaTime);
        break;
      case 'chase':
        this.executeChase(deltaTime, playerPosition);
        break;
      case 'attack':
        this.executeAttack(deltaTime, playerPosition, elapsedTime);
        break;
      case 'evade':
        this.executeEvade(deltaTime, playerPosition);
        break;
    }

    // Apply velocity limits
    if (this.type !== 'station') {
      const speed = this.physicsBody.velocity.length();
      if (speed > this.maxSpeed) {
        this.physicsBody.velocity.normalize().multiplyScalar(this.maxSpeed);
      }
    }
  }

  private executePatrol(deltaTime: number): void {
    if (this.type === 'station') return;

    // Orbit around patrol center
    this.patrolAngle += deltaTime * 0.3;
    const targetX = this.patrolCenter.x + Math.cos(this.patrolAngle) * this.patrolRadius;
    const targetZ = this.patrolCenter.z + Math.sin(this.patrolAngle) * this.patrolRadius;
    const targetY = this.patrolCenter.y + Math.sin(this.patrolAngle * 0.5) * 10;

    this.aiTarget.set(targetX, targetY, targetZ);
    this.moveToward(this.aiTarget, deltaTime, 0.5);
  }

  private executeChase(deltaTime: number, playerPosition: THREE.Vector3): void {
    if (this.type === 'station') return;

    // Move toward player
    this.aiTarget.copy(playerPosition);
    this.moveToward(this.aiTarget, deltaTime, 1.0);
  }

  private executeAttack(deltaTime: number, playerPosition: THREE.Vector3, elapsedTime: number): void {
    const myPosition = this.mesh.position;

    // Strafe around player while attacking (fighters)
    if (this.type === 'fighter') {
      const angleToPlayer = Math.atan2(
        playerPosition.z - myPosition.z,
        playerPosition.x - myPosition.x
      );
      const strafeAngle = angleToPlayer + Math.PI / 2;
      const strafeOffset = new THREE.Vector3(
        Math.cos(strafeAngle) * 30,
        Math.sin(elapsedTime * 2) * 10,
        Math.sin(strafeAngle) * 30
      );
      this.aiTarget.copy(playerPosition).add(strafeOffset);
      this.moveToward(this.aiTarget, deltaTime, 0.7);
    } else if (this.type === 'cruiser') {
      // Cruisers try to maintain attack range distance
      const dirToPlayer = new THREE.Vector3().subVectors(playerPosition, myPosition).normalize();
      const desiredPos = playerPosition.clone().sub(dirToPlayer.multiplyScalar(this.attackRange * 0.8));
      this.moveToward(desiredPos, deltaTime, 0.5);
    }

    // Fire at player
    this.tryFire(playerPosition, elapsedTime);
  }

  private executeEvade(deltaTime: number, playerPosition: THREE.Vector3): void {
    if (this.type === 'station') return;

    // Run away from player
    const myPosition = this.mesh.position;
    const awayDir = new THREE.Vector3().subVectors(myPosition, playerPosition).normalize();

    // Add some randomness to evade direction
    awayDir.x += Math.sin(Date.now() * 0.003) * 0.3;
    awayDir.z += Math.cos(Date.now() * 0.004) * 0.3;
    awayDir.normalize();

    this.aiTarget.copy(myPosition).add(awayDir.multiplyScalar(50));
    this.moveToward(this.aiTarget, deltaTime, 1.2);
  }

  private moveToward(target: THREE.Vector3, deltaTime: number, speedMultiplier: number = 1): void {
    const myPosition = this.mesh.position;
    const direction = new THREE.Vector3().subVectors(target, myPosition);
    const distance = direction.length();

    if (distance > 1) {
      direction.normalize();
      const accel = direction.multiplyScalar(this.acceleration * speedMultiplier);
      this.physicsBody.velocity.add(accel.multiplyScalar(deltaTime));
    }
  }

  private tryFire(playerPosition: THREE.Vector3, elapsedTime: number): void {
    if (elapsedTime - this.lastFireTime < this.fireRate) return;
    if (!this.onFireProjectile) return;

    this.lastFireTime = elapsedTime;

    const myPosition = this.mesh.position.clone();
    const direction = new THREE.Vector3().subVectors(playerPosition, myPosition).normalize();

    // Add some inaccuracy based on type
    const inaccuracy = this.type === 'fighter' ? 0.1 : (this.type === 'cruiser' ? 0.05 : 0.15);
    direction.x += (Math.random() - 0.5) * inaccuracy;
    direction.y += (Math.random() - 0.5) * inaccuracy;
    direction.z += (Math.random() - 0.5) * inaccuracy;
    direction.normalize();

    // Spawn position in front of the enemy
    const spawnPos = myPosition.add(direction.clone().multiplyScalar(this.getRadius() + 2));

    this.onFireProjectile(spawnPos, direction, this.physicsBody.velocity.clone());
  }

  public getAIState(): AIState {
    return this.aiState;
  }

  private billboardToCamera(obj: THREE.Object3D, cameraPosition: THREE.Vector3): void {
    const worldPos = new THREE.Vector3();
    obj.getWorldPosition(worldPos);

    const lookAt = cameraPosition.clone();
    lookAt.y = worldPos.y; // Keep upright

    obj.lookAt(lookAt);
  }

  public takeDamage(amount: number = 1): boolean {
    if (!this.alive) return false;

    this.health -= amount;

    if (this.health <= 0) {
      this.health = 0;
      this.alive = false;
      return true; // Destroyed
    }

    return false; // Still alive
  }

  public isAlive(): boolean {
    return this.alive;
  }

  public getHealth(): number {
    return this.health;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public dispose(): void {
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Line) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
